import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useAppStore } from '@core/store'
import {
  FUNDOS,
  TRAMAS,
  type EstiloDeCorte,
  type RegraDeCorte,
  type Trama,
} from './estilosDeCorte'
import { amostraDaTrama } from './amostraDaTrama'
import { lerLentes, ouvirLentes, type Lente } from '@viewer/lens/lensRules'

/** As lentes guardadas, sempre em dia (o painel das lentes grava-as). */
function useLentesGuardadas(): Lente[] {
  const [lentes, setLentes] = useState<Lente[]>(lerLentes)
  useEffect(() => ouvirLentes(() => setLentes(lerLentes())), [])
  return lentes
}

const SELECT =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[10px] text-[var(--color-text)]'
const COR = 'h-5 w-6 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0'
const ICONE =
  'rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] disabled:opacity-30'

/** Fundo, trama e cor do traço de um estilo, em linha. */
function EditorDoEstilo({
  estilo,
  mudar,
  comFundosComNome = false,
}: {
  estilo: EstiloDeCorte
  mudar: (m: Partial<EstiloDeCorte>) => void
  comFundosComNome?: boolean
}) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span
        className="inline-block h-5 w-8 shrink-0 rounded border border-[var(--color-border)]"
        style={amostraDaTrama(estilo)}
        aria-hidden
      />
      <select
        value={estilo.trama}
        onChange={(e) => mudar({ trama: e.target.value as Trama })}
        className={SELECT}
        title="Trama"
        aria-label="Trama"
      >
        {TRAMAS.map((t) => (
          <option key={t.valor} value={t.valor}>
            {t.rotulo}
          </option>
        ))}
      </select>
      {comFundosComNome &&
        FUNDOS.map((f) => (
          <button
            key={f.valor}
            type="button"
            onClick={() => mudar({ fundo: f.valor })}
            aria-pressed={estilo.fundo === f.valor}
            title={`Fundo ${f.rotulo.toLowerCase()}`}
            className={`rounded border px-1.5 py-0.5 text-[10px] ${
              estilo.fundo === f.valor
                ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {f.rotulo}
          </button>
        ))}
      <input
        type="color"
        value={estilo.fundo}
        onChange={(e) => mudar({ fundo: e.target.value })}
        title="Cor de fundo"
        aria-label="Cor de fundo"
        className={COR}
      />
      {estilo.trama !== 'cheio' && (
        <input
          type="color"
          value={estilo.corDaTrama}
          onChange={(e) => mudar({ corDaTrama: e.target.value })}
          title="Cor da trama"
          aria-label="Cor da trama"
          className={COR}
        />
      )}
    </span>
  )
}

/** Estilo do corte: o base e as regras por classe ou material. */
export default function PainelDeEstilosDeCorte() {
  const base = useAppStore((s) => s.estiloBaseDeCorte)
  const setBase = useAppStore((s) => s.setEstiloBaseDeCorte)
  const regras = useAppStore((s) => s.regrasDeCorte)
  const acrescentar = useAppStore((s) => s.acrescentarRegraDeCorte)
  const sugeridas = useAppStore((s) => s.juntarRegrasSugeridas)

  return (
    <details className="mb-2 rounded border border-[var(--color-border)] px-2 py-1" data-campo="estilos-de-corte">
      <summary className="cursor-pointer select-none text-[10px] text-[var(--color-text-muted)]">
        Estilo do corte
        {regras.length > 0 && (
          <span className="text-[var(--color-text-dim)]">
            {' '}
            ({regras.filter((r) => r.activa).length} regra{regras.filter((r) => r.activa).length === 1 ? '' : 's'})
          </span>
        )}
      </summary>
      <div className="mt-1.5 flex flex-col gap-1.5 text-[10px] text-[var(--color-text-muted)]">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="w-10 shrink-0">Base</span>
          <EditorDoEstilo estilo={base} mudar={setBase} comFundosComNome />
        </div>
        <p className="text-[var(--color-text-dim)]">
          O base vale para tudo o que nenhuma regra apanha. As regras vão por
          ordem: a primeira que apanha um elemento decide o estilo dele.
        </p>
        {regras.length > 0 && (
          <ul className="flex flex-col gap-1">
            {regras.map((r, i) => (
              <Regra key={r.id} regra={r} primeira={i === 0} ultima={i === regras.length - 1} />
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-1">
          <button type="button" onClick={() => acrescentar('classe')} className={`${SELECT} hover:text-[var(--color-text)]`}>
            + Por classe
          </button>
          <button type="button" onClick={() => acrescentar('material')} className={`${SELECT} hover:text-[var(--color-text)]`}>
            + Por material
          </button>
          <button
            type="button"
            onClick={() => acrescentar('lente')}
            title="Usar uma lente guardada: os elementos que alguma regra activa da lente apanhe (por classe, nome ou modelo) ficam com este estilo."
            className={`${SELECT} hover:text-[var(--color-text)]`}
          >
            + Por lente
          </button>
          <button
            type="button"
            onClick={sugeridas}
            title="Betão pontilhado, alvenaria e madeira a diagonal, isolamento em ondas, aço a cheio, vidro azul claro. Ajusta depois os nomes de material ao que o modelo usa."
            className={`${SELECT} hover:text-[var(--color-text)]`}
          >
            + Sugeridas
          </button>
        </div>
        {regras.some((r) => r.tipo === 'material') && (
          <p className="text-[var(--color-text-dim)]">
            O material lê-se do IFC (IfcRelAssociatesMaterial do elemento, ou do
            tipo): o nome do material, das camadas, dos perfis e dos
            constituintes. Um modelo aberto de um .frag só tem as classes.
          </p>
        )}
      </div>
    </details>
  )
}

function Regra({ regra, primeira, ultima }: { regra: RegraDeCorte; primeira: boolean; ultima: boolean }) {
  const actualizar = useAppStore((s) => s.actualizarRegraDeCorte)
  const tirar = useAppStore((s) => s.tirarRegraDeCorte)
  const mover = useAppStore((s) => s.moverRegraDeCorte)
  const lentes = useLentesGuardadas()
  const lenteApagada = regra.tipo === 'lente' && regra.valor !== '' && !lentes.some((l) => l.id === regra.valor)
  return (
    <li className="flex flex-col gap-1 rounded border border-[var(--color-border)] px-1.5 py-1" data-campo="regra-de-corte">
      <div className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={regra.activa}
          onChange={(e) => actualizar(regra.id, { activa: e.target.checked })}
          className="accent-[var(--color-accent)]"
          title="Ligar ou desligar esta regra"
        />
        <select
          value={regra.tipo}
          onChange={(e) => {
            const tipo = e.target.value as RegraDeCorte['tipo']
            // O valor de uma regra de lente é um id; de classe ou material,
            // texto. Trocar de tipo não pode deixar um no lugar do outro.
            const valor = tipo === 'lente' ? (lentes[0]?.id ?? '') : regra.tipo === 'lente' ? '' : regra.valor
            actualizar(regra.id, { tipo, valor })
          }}
          className={SELECT}
          aria-label="Tipo de regra"
        >
          <option value="classe">Classe</option>
          <option value="material">Material</option>
          <option value="lente">Lente</option>
        </select>
        {regra.tipo === 'lente' ? (
          <select
            value={lenteApagada ? '' : regra.valor}
            onChange={(e) => actualizar(regra.id, { valor: e.target.value })}
            aria-label="Lente"
            title="A lente cujas regras activas decidem que elementos ficam com este estilo"
            className={`${SELECT} min-w-0 flex-1`}
          >
            <option value="" disabled>
              {lentes.length === 0 ? 'Não há lentes guardadas' : lenteApagada ? 'A lente foi apagada' : 'Escolhe uma lente'}
            </option>
            {lentes.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nome}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={regra.valor}
            onChange={(e) => actualizar(regra.id, { valor: e.target.value })}
            placeholder={regra.tipo === 'classe' ? 'IfcWall' : 'betão, concrete'}
            title={
              regra.tipo === 'classe'
                ? 'Classe IFC. "IfcWall" apanha também IfcWallStandardCase.'
                : 'Pedaços do nome do material, separados por vírgulas. Sem distinguir maiúsculas nem acentos.'
            }
            aria-label="Valor da regra"
            className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[10px] text-[var(--color-text)]"
          />
        )}
        <button type="button" onClick={() => mover(regra.id, -1)} disabled={primeira} title="Subir (ganha às de baixo)" className={ICONE}>
          <ArrowUp size={10} />
        </button>
        <button type="button" onClick={() => mover(regra.id, 1)} disabled={ultima} title="Descer" className={ICONE}>
          <ArrowDown size={10} />
        </button>
        <button
          type="button"
          onClick={() => tirar(regra.id)}
          title="Tirar esta regra"
          className={`${ICONE} hover:text-[var(--color-error-text)]`}
        >
          <Trash2 size={10} />
        </button>
      </div>
      <EditorDoEstilo estilo={regra.estilo} mudar={(m) => actualizar(regra.id, { estilo: { ...regra.estilo, ...m } })} />
    </li>
  )
}
